import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import ExpenseService from '../services/ExpenseService';

const initialState = {
    expenses: [],
    headers: [],
    subheaders: [],
    categories: [], // Grouped categories for the expense form
    pagination: {
        page: 0,
        per_page: 10,
        total: 0,
        totalPages: 0,
    },
    loading: false,
    submitLoading: false,
    isSuccess: false,
    formError: [],
    grand_total: 0,
};

// --- Expense Actions ---
export const fetchExpenses = createAsyncThunk(
    'expense/fetchExpenses',
    async (params, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.List(params);
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const createExpense = createAsyncThunk(
    'expense/createExpense',
    async (data, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.Create(data);
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const updateExpense = createAsyncThunk(
    'expense/updateExpense',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.Put(id, data);
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const deleteExpense = createAsyncThunk(
    'expense/deleteExpense',
    async (id, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.Delete(id);
            return { id, data: resp.data };
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const changeExpenseStatus = createAsyncThunk(
    'expense/changeStatus',
    async ({ id, status }, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.ChangeStatus(id, status);
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const fetchExpenseCategories = createAsyncThunk(
    'expense/fetchCategories',
    async (_, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.GetCategories();
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

// --- Header Actions ---
export const fetchExpenseHeaders = createAsyncThunk(
    'expense/fetchHeaders',
    async (_, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.GetHeaders();
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const createExpenseHeader = createAsyncThunk(
    'expense/createHeader',
    async (data, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.CreateHeader(data);
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const updateExpenseHeader = createAsyncThunk(
    'expense/updateHeader',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.UpdateHeader(id, data);
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const deleteExpenseHeader = createAsyncThunk(
    'expense/deleteHeader',
    async (id, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.DeleteHeader(id);
            return { id, data: resp.data };
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

// --- Subheader Actions ---
export const fetchExpenseSubheaders = createAsyncThunk(
    'expense/fetchSubheaders',
    async (_, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.GetSubheaders();
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const createExpenseSubheader = createAsyncThunk(
    'expense/createSubheader',
    async (data, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.CreateSubheader(data);
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const updateExpenseSubheader = createAsyncThunk(
    'expense/updateSubheader',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.UpdateSubheader(id, data);
            return resp;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

export const deleteExpenseSubheader = createAsyncThunk(
    'expense/deleteSubheader',
    async (id, { rejectWithValue }) => {
        try {
            const resp = await ExpenseService.DeleteSubheader(id);
            return { id, data: resp.data };
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

const expenseSlice = createSlice({
    name: 'expense',
    initialState,
    reducers: {
        resetStatus: (state) => {
            state.isSuccess = false;
            state.formError = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Expenses
            .addCase(fetchExpenses.pending, (state) => { state.loading = true; })
            .addCase(fetchExpenses.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.status === 200) {
                    state.expenses = action.payload.data.data.expenses;
                    state.pagination = action.payload.data.data.pagination;
                    state.grand_total = action.payload.data.data.grand_total || 0;
                }
            })
            .addCase(fetchExpenses.rejected, (state) => { state.loading = false; })

            .addCase(createExpense.pending, (state) => { state.submitLoading = true; state.isSuccess = false; state.formError = []; })
            .addCase(createExpense.fulfilled, (state, action) => {
                state.submitLoading = false;
                const httpOk = action.payload?.status === 200 || action.payload?.status === 201;
                const apiOk = action.payload?.data?.status === 'Success';
                if (httpOk || apiOk) {
                    state.isSuccess = true;
                    const expense = action.payload?.data?.data;
                    if (expense) state.expenses.unshift(expense);
                    toast.success(action.payload?.data?.message || 'Expense created successfully');
                }
            })
            .addCase(createExpense.rejected, (state, action) => {
                state.submitLoading = false;
                state.formError = action.payload?.data?.errors || [];
                toast.error(action.payload?.data?.message || 'Failed to create expense');
            })

            .addCase(updateExpense.pending, (state) => { state.submitLoading = true; state.isSuccess = false; state.formError = []; })
            .addCase(updateExpense.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200) {
                    state.isSuccess = true;
                    const index = state.expenses.findIndex((e) => e.id === action.payload.data.data.id);
                    if (index !== -1) state.expenses[index] = action.payload.data.data;
                    toast.success(action.payload.data.message);
                }
            })
            .addCase(updateExpense.rejected, (state, action) => {
                state.submitLoading = false;
                state.formError = action.payload?.data?.errors || [];
            })

            .addCase(deleteExpense.fulfilled, (state, action) => {
                state.expenses = state.expenses.filter((e) => e.id !== action.payload.id);
                toast.success(action.payload.data.message);
            })

            .addCase(changeExpenseStatus.pending, (state) => { state.submitLoading = true; state.isSuccess = false; })
            .addCase(changeExpenseStatus.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200) {
                    state.isSuccess = true;
                    const index = state.expenses.findIndex((e) => e.id === action.payload.data.data.id);
                    if (index !== -1) state.expenses[index] = action.payload.data.data;
                    toast.success(action.payload.data.message);
                }
            })
            .addCase(changeExpenseStatus.rejected, (state, action) => {
                state.submitLoading = false;
                toast.error(action.payload?.data?.message || 'Failed to change status');
            })

            .addCase(fetchExpenseCategories.fulfilled, (state, action) => {
                if (action.payload?.status === 200) state.categories = action.payload.data.data;
            })

            // Headers
            .addCase(fetchExpenseHeaders.pending, (state) => { state.loading = true; })
            .addCase(fetchExpenseHeaders.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.status === 200) state.headers = action.payload.data.data;
            })
            .addCase(fetchExpenseHeaders.rejected, (state) => { state.loading = false; })

            .addCase(createExpenseHeader.pending, (state) => { state.submitLoading = true; state.isSuccess = false; state.formError = []; })
            .addCase(createExpenseHeader.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200) {
                    state.isSuccess = true;
                    state.headers.push(action.payload.data.data);
                    toast.success(action.payload.data.message);
                }
            })
            .addCase(createExpenseHeader.rejected, (state, action) => {
                state.submitLoading = false;
                state.formError = action.payload?.data?.errors || [];
            })

            .addCase(updateExpenseHeader.pending, (state) => { state.submitLoading = true; state.isSuccess = false; state.formError = []; })
            .addCase(updateExpenseHeader.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200) {
                    state.isSuccess = true;
                    const index = state.headers.findIndex((h) => h.id === action.payload.data.data.id);
                    if (index !== -1) state.headers[index] = action.payload.data.data;
                    toast.success(action.payload.data.message);
                }
            })
            .addCase(updateExpenseHeader.rejected, (state, action) => {
                state.submitLoading = false;
                state.formError = action.payload?.data?.errors || [];
            })

            .addCase(deleteExpenseHeader.fulfilled, (state, action) => {
                state.headers = state.headers.filter((h) => h.id !== action.payload.id);
                toast.success(action.payload.data.message);
            })

            // Subheaders
            .addCase(fetchExpenseSubheaders.pending, (state) => { state.loading = true; })
            .addCase(fetchExpenseSubheaders.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.status === 200) state.subheaders = action.payload.data.data;
            })
            .addCase(fetchExpenseSubheaders.rejected, (state) => { state.loading = false; })

            .addCase(createExpenseSubheader.pending, (state) => { state.submitLoading = true; state.isSuccess = false; state.formError = []; })
            .addCase(createExpenseSubheader.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200) {
                    state.isSuccess = true;
                    state.subheaders.push(action.payload.data.data);
                    toast.success(action.payload.data.message);
                }
            })
            .addCase(createExpenseSubheader.rejected, (state, action) => {
                state.submitLoading = false;
                state.formError = action.payload?.data?.errors || [];
            })

            .addCase(updateExpenseSubheader.pending, (state) => { state.submitLoading = true; state.isSuccess = false; state.formError = []; })
            .addCase(updateExpenseSubheader.fulfilled, (state, action) => {
                state.submitLoading = false;
                if (action.payload?.status === 200) {
                    state.isSuccess = true;
                    const index = state.subheaders.findIndex((s) => s.id === action.payload.data.data.id);
                    if (index !== -1) state.subheaders[index] = action.payload.data.data;
                    toast.success(action.payload.data.message);
                }
            })
            .addCase(updateExpenseSubheader.rejected, (state, action) => {
                state.submitLoading = false;
                state.formError = action.payload?.data?.errors || [];
            })

            .addCase(deleteExpenseSubheader.fulfilled, (state, action) => {
                state.subheaders = state.subheaders.filter((s) => s.id !== action.payload.id);
                toast.success(action.payload.data.message);
            });
    },
});

export const { resetStatus } = expenseSlice.actions;
export default expenseSlice.reducer;
